import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface TaskNode {
  id: string;
  name: string;
  position: [number, number, number];
  status: 'pending' | 'running' | 'completed' | 'error';
  agentId?: string;
}

interface TaskConnection {
  from: string;
  to: string;
  strength: number;
}

interface TaskFlow3DProps {
  nodes: TaskNode[];
  connections: TaskConnection[];
  animate?: boolean;
}

export function TaskFlow3D({ nodes, connections, animate = true }: TaskFlow3DProps) {
  const groupRef = useRef<THREE.Group>(null);

  const nodePositions = useMemo(() => {
    return nodes.reduce((acc, node) => {
      acc[node.id] = node.position;
      return acc;
    }, {} as Record<string, [number, number, number]>);
  }, [nodes]);

  const getStatusColor = (status: TaskNode['status']) => {
    switch (status) {
      case 'pending': return '#fbbf24';
      case 'running': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusSize = (status: TaskNode['status']) => {
    switch (status) {
      case 'running': return 0.4;
      case 'completed': return 0.35;
      case 'error': return 0.45;
      default: return 0.3;
    }
  };

  useFrame((state) => {
    if (groupRef.current && animate) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Connection lines */}
      {connections.map((connection, index) => {
        const fromPos = nodePositions[connection.from];
        const toPos = nodePositions[connection.to];
        
        if (!fromPos || !toPos) return null;

        return (
          <line key={index}>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([
                  fromPos[0], fromPos[1], fromPos[2],
                  toPos[0], toPos[1], toPos[2]
                ])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color={0x6366f1} transparent opacity={0.6} />
          </line>
        );
      })}

      {/* Task nodes */}
      {nodes.map((node) => (
        <group key={node.id} position={node.position}>
          <mesh>
            <sphereGeometry args={[getStatusSize(node.status), 16, 16]} />
            <meshStandardMaterial 
              color={getStatusColor(node.status)}
              transparent 
              opacity={0.8}
              metalness={0.3}
              roughness={0.2}
            />
          </mesh>
          
          {/* Pulse effect for running tasks */}
          {node.status === 'running' && (
            <mesh>
              <sphereGeometry args={[getStatusSize(node.status) * 1.5, 16, 16]} />
              <meshStandardMaterial 
                color={getStatusColor(node.status)}
                transparent 
                opacity={0.3}
                metalness={0.1}
                roughness={0.8}
              />
            </mesh>
          )}
        </group>
      ))}
    </group>
  );
}

// Task flow with sample data
export function SampleTaskFlow3D() {
  const sampleNodes: TaskNode[] = [
    {
      id: '1',
      name: 'Data Collection',
      position: [-3, 0, 0],
      status: 'completed'
    },
    {
      id: '2',
      name: 'Data Processing',
      position: [0, 2, 0],
      status: 'running',
      agentId: 'agent-1'
    },
    {
      id: '3',
      name: 'Analysis',
      position: [3, 0, 0],
      status: 'pending'
    },
    {
      id: '4',
      name: 'Report Generation',
      position: [0, -2, 0],
      status: 'pending'
    }
  ];

  const sampleConnections: TaskConnection[] = [
    { from: '1', to: '2', strength: 1 },
    { from: '2', to: '3', strength: 0.8 },
    { from: '3', to: '4', strength: 0.6 },
    { from: '2', to: '4', strength: 0.4 }
  ];

  return (
    <TaskFlow3D 
      nodes={sampleNodes} 
      connections={sampleConnections}
      animate={true}
    />
  );
}
